import { useEffect, useMemo, useState } from 'react';
import { useLocaleStore } from '@/lib/core/stores';
import type { EventListItemDto, GetEventsV1Params } from '@/services/platform/api/model';
import { getEvents } from './events';
import { dateKeyRange, toDateKey } from './types';

/** Max page size accepted by the API. */
const DEFAULT_PAGE_SIZE = 200;

export interface UseEventsByDayResult {
  /**
   * Day-bucketed items, ascending by day key and, within a day, by start time. An event appears
   * under every day it happens on — a campaign under each day its recurrence runs, a span under each
   * day it covers — and each copy carries THAT day's `startDateTime`/`endDateTime` and status (see
   * `cardForDay`), so the card shows the day's own hour and sold-out state.
   */
  days: [string, EventListItemDto[]][];
  /** Deduped union of every bucket — what the category pills count over. */
  events: EventListItemDto[];
  isLoading: boolean;
  hasFetched: boolean;
}

/** One day an event is listed under, with the slot that puts it there. */
interface EventDaySlot {
  date: string;
  startDateTime?: string | null;
  endDateTime?: string | null;
  /** That day's status — its occurrence's, else the event's. Absent without `days[]`. */
  status?: string | null;
}

/**
 * The days an event is listed under. `days[]` from the server is the rule — the same one the rail
 * (`/events/dates`) lights days by, already floored at today and clipped to the date filters, and
 * the only source of a campaign's future days (Salesforce sends none). Without it (a server older
 * than `days[]`) the event's own start→end span stands in, floored at today (Europe/Lisbon).
 */
function eventDaySlots(event: EventListItemDto, todayKey: string | undefined): EventDaySlot[] {
  if (event.days) {
    return event.days
      .filter((day): day is typeof day & { date: string } => !!day.date)
      .map(day => ({
        date: day.date,
        startDateTime: day.startDateTime,
        endDateTime: day.endDateTime,
        status: day.status,
      }));
  }
  return dateKeyRange(event.startDateTime, event.endDateTime)
    .filter(key => !todayKey || key >= todayKey)
    .map(date => ({ date, startDateTime: event.startDateTime, endDateTime: event.endDateTime }));
}

/** Whether two ISO date-times are the same instant (the server's 7-digit fractions included). */
function isSameInstant(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return false;
  const [ta, tb] = [new Date(a).getTime(), new Date(b).getTime()];
  return !Number.isNaN(ta) && ta === tb;
}

/**
 * The card listed under one day. It shows that day's slot — a campaign's Friday hour, not its
 * long-past original start — and that day's status: a `Full` day reads sold out on its own, while
 * the event's other days stay open. The card's `status` and seat count describe the date it is
 * dated by, so the count is kept only on that slot (a span's days all share it) and dropped
 * elsewhere rather than showing another date's seats.
 */
function cardForDay(event: EventListItemDto, slot: EventDaySlot): EventListItemDto {
  const isDatedSlot = isSameInstant(slot.startDateTime, event.startDateTime);
  return {
    ...event,
    startDateTime: slot.startDateTime,
    endDateTime: slot.endDateTime,
    status: slot.status ?? event.status,
    availableSpots: isDatedSlot ? event.availableSpots : null,
    hasAvailableDates: slot.status === 'Full' ? false : event.hasAvailableDates,
  };
}

/** Orders a day's cards by start time — undated ones last, in the server's order (the sort is stable). */
function byStartTime(a: EventListItemDto, b: EventListItemDto): number {
  const timeOf = (event: EventListItemDto) =>
    event.startDateTime ? new Date(event.startDateTime).getTime() : NaN;
  const [ta, tb] = [timeOf(a), timeOf(b)];
  if (Number.isNaN(ta)) return Number.isNaN(tb) ? 0 : 1;
  if (Number.isNaN(tb)) return -1;
  return ta - tb;
}

/**
 * Day bucketing for the ELP list: one page of upcoming events, grouped into the days each one
 * happens on from today (Europe/Lisbon) — never a day already past. Campaigns are part of it (no
 * separate request): each lands under every day its recurrence runs. The rail marks a day and
 * scrolls to its block rather than filtering, so the list always holds the whole upcoming set and
 * there is no per-day request to make.
 */
export function useEventsByDay(params: GetEventsV1Params): UseEventsByDayResult {
  const locale = useLocaleStore(state => state.locale);
  const [days, setDays] = useState<[string, EventListItemDto[]][]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);

  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    async function run() {
      setIsLoading(true);
      try {
        const result = await getEvents({ pageSize: DEFAULT_PAGE_SIZE, ...params }, locale, controller.signal);
        if (cancelled) return;
        // Today in Europe/Lisbon — the floor the server and the rail (`/events/dates`) use. Read per
        // fetch, not per mount, so a list left open past midnight floors at the new day.
        const todayKey = toDateKey(new Date().toISOString());
        const map = new Map<string, EventListItemDto[]>();
        for (const event of result?.items ?? []) {
          for (const slot of eventDaySlots(event, todayKey)) {
            const item = cardForDay(event, slot);
            const bucket = map.get(slot.date);
            if (bucket) bucket.push(item);
            else map.set(slot.date, [item]);
          }
        }
        for (const bucket of map.values()) bucket.sort(byStartTime);
        setDays([...map.entries()].sort(([a], [b]) => a.localeCompare(b)));
      } catch (error) {
        /*
         * `run()` is invoked without a `.catch`, so anything thrown in here became an unhandled
         * promise rejection — including the `AbortError` the cleanup below raises on every filter
         * change and unmount, which is not a failure at all.
         *
         * A cancel is left silent; a real one leaves `days` alone rather than blanking the list,
         * since the previous day's events are better than an empty screen, and `hasFetched` still
         * flips in `finally` so the caller stops waiting.
         */
        if (cancelled || (error instanceof Error && error.name === 'AbortError')) return;
        console.error('useEventsByDay: failed to fetch events', error);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setHasFetched(true);
        }
      }
    }

    run();
    return () => {
      cancelled = true;
      controller.abort();
    };
    // paramsKey captures the params object contents; params itself is intentionally omitted.
  }, [paramsKey, locale]);

  const events = useMemo(() => {
    const seen = new Set<string>();
    const all: EventListItemDto[] = [];
    for (const [, items] of days) {
      for (const event of items) {
        if (event.id) {
          if (seen.has(event.id)) continue;
          seen.add(event.id);
        }
        all.push(event);
      }
    }
    return all;
  }, [days]);

  return { days, events, isLoading, hasFetched };
}
